<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

type Status = 'idle' | 'submitting' | 'sent' | 'error';

const STORAGE_KEY = 'newsletterConsentSubmitted';
const NEWSLETTER_SOURCE = 'notam-briefer';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const visible = ref(false);
const status = ref<Status>('idle');
const email = ref<string>('');
const emailError = ref<boolean>(false);
const sentMessageKey = ref<string>('newsletterBannerSent');

onMounted(() => {
  try {
    if (localStorage.getItem(STORAGE_KEY) === '1') return;
  } catch { /* localStorage unavailable — show anyway */ }
  visible.value = true;
});

function markSubmitted(): void {
  try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
}

function messageKeyForState(state: string | undefined): string {
  if (state === 'alreadySubscribed') return 'newsletterBannerAlreadyKnown';
  if (state === 'resubscribed') return 'newsletterBannerResubscribed';
  return 'newsletterBannerSent';
}

async function submitConsent(value: boolean): Promise<void> {
  if (value) {
    if (!EMAIL_RE.test(email.value.trim())) {
      emailError.value = true;
      return;
    }
    emailError.value = false;
  }
  status.value = 'submitting';
  try {
    const res = await fetch('/api/marketingConsent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: value ? email.value.trim() : 'opt-out@local.invalid',
        value,
        source: NEWSLETTER_SOURCE,
        locale: locale.value
      })
    });
    if (!res.ok) throw new Error('consent_failed');
    const data = await res.json().catch(() => ({}));
    markSubmitted();
    if (!value) {
      visible.value = false;
      return;
    }
    sentMessageKey.value = messageKeyForState(data?.state);
    status.value = 'sent';
    setTimeout(() => { visible.value = false; }, 5000);
  } catch {
    status.value = 'error';
  }
}
</script>

<template>
  <transition name="slide-up">
    <div
      v-if="visible"
      class="newsletter-banner"
      role="dialog"
      :aria-label="t('newsletterBannerTitle')"
    >
      <div class="newsletter-banner__inner">
        <p class="newsletter-banner__title">{{ t('newsletterBannerTitle') }}</p>

        <template v-if="status === 'sent'">
          <p class="newsletter-banner__body">{{ t(sentMessageKey) }}</p>
        </template>
        <template v-else-if="status === 'error'">
          <p class="newsletter-banner__body newsletter-banner__body--error">
            {{ t('newsletterBannerError') }}
          </p>
        </template>
        <template v-else>
          <p class="newsletter-banner__body">{{ t('newsletterBannerBody') }}</p>
          <input
            type="email"
            inputmode="email"
            autocomplete="email"
            v-model="email"
            :placeholder="t('newsletterBannerEmailPlaceholder')"
            class="newsletter-banner__input"
            :class="{ 'newsletter-banner__input--error': emailError }"
            :disabled="status === 'submitting'"
          />
          <div class="newsletter-banner__actions">
            <button
              class="newsletter-banner__btn newsletter-banner__btn--secondary"
              :disabled="status === 'submitting'"
              @click="submitConsent(false)"
            >
              {{ t('newsletterBannerNo') }}
            </button>
            <button
              class="newsletter-banner__btn newsletter-banner__btn--primary"
              :disabled="status === 'submitting'"
              @click="submitConsent(true)"
            >
              {{ t('newsletterBannerYes') }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.newsletter-banner {
  position: fixed;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 50;
  padding: 0 1rem;
  pointer-events: none;
}
.newsletter-banner__inner {
  pointer-events: auto;
  max-width: 28rem;
  margin: 0 auto;
  background: #1F2859;
  border: 1px solid #575E83;
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
  padding: 1rem 1.25rem;
  color: #C7C9D6;
}
.newsletter-banner__title {
  color: #FFFFFF;
  font-weight: 600;
  font-size: 0.95rem;
  margin: 0 0 0.25rem 0;
}
.newsletter-banner__body {
  font-size: 0.85rem;
  font-weight: 300;
  line-height: 1.4;
  margin: 0 0 0.75rem 0;
}
.newsletter-banner__body--error { color: #fca5a5; }
.newsletter-banner__input {
  width: 100%;
  background: #0F1538;
  border: 1px solid #575E83;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin: 0 0 0.75rem 0;
  color: #FFFFFF;
  font-size: 0.85rem;
}
.newsletter-banner__input:focus {
  outline: none;
  border-color: #E1EFF2;
}
.newsletter-banner__input--error { border-color: #fca5a5; }
.newsletter-banner__actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
.newsletter-banner__btn {
  border-radius: 9999px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 500;
  border: 0;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}
.newsletter-banner__btn:disabled { opacity: 0.6; cursor: wait; }
.newsletter-banner__btn--primary {
  background: #E1EFF2;
  color: #1F2859;
}
.newsletter-banner__btn--primary:hover:not(:disabled) { background: #C7C9D6; }
.newsletter-banner__btn--secondary {
  background: transparent;
  color: #C7C9D6;
  border: 1px solid #575E83;
}
.newsletter-banner__btn--secondary:hover:not(:disabled) { border-color: #E1EFF2; color: #FFFFFF; }

.slide-up-enter-active, .slide-up-leave-active { transition: transform 0.3s ease, opacity 0.3s ease; }
.slide-up-enter-from, .slide-up-leave-to { transform: translateY(120%); opacity: 0; }
</style>
